import { registerLocaleData } from '@angular/common';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import ar from '@angular/common/locales/ar';
import en from '@angular/common/locales/en';
import type {
  ApplicationConfig,
  EnvironmentProviders,
  Provider,
} from '@angular/core';
import {
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideSignalFormsConfig } from '@angular/forms/signals';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import {
  isAnalyticsDisabledForEnv,
  provideAnalytics,
} from '@indutrust/analytics-data';
import {
  AuthApiClient,
  authInterceptor,
  AuthSessionStore,
} from '@indutrust/auth-data';
import { AUTH_STRATEGY } from '@indutrust/auth-model';
import { provideCharts } from '@indutrust/chart-util';
import { CompanyStore } from '@indutrust/company-data';
import {
  CompanyFactoriesStore,
  factoryLegacyGapHandlers,
} from '@indutrust/factory-data';
import { provideIndutrustBrandConfig } from '@indutrust/indutrust-config';
import {
  CountryStore,
  I18nStore,
  IndustrialTypeStore,
  LayoutStore,
  PlatformStore,
  RouterStore,
  TechnologyStore,
  ThemeStore,
} from '@indutrust/shared-data';
import { provideEnvironment } from '@indutrust/shared-env';
import {
  mockBackendInterceptor,
  provideMockBackendInterceptor,
} from '@indutrust/shared-mock-backend';
import { MessageService } from 'primeng/api';
import { environment } from '../environments/environment';
import { AppStore } from './+state/app.store';
import { appRoutes } from './app.routes';
import { translocoEnvironmentProvider } from './shared/i18n/transloco-provider';

registerLocaleData(en);
registerLocaleData(ar);

const useLegacyGapMocks =
  environment.appEnv === 'local' && !environment.demoMode;

/**
 * Creates the Angular {@link ApplicationConfig} for the Indutrust frontend application.
 *
 * The configuration wires core providers such as routing, HTTP with interceptors, animations,
 * zoneless change detection, i18n, and all application signal stores (e.g., {@link AppStore}, {@link AuthSessionStore}).
 *
 * @returns The application configuration object to bootstrap the app.
 * @example
 * import { bootstrapApplication } from '@angular/platform-browser';
 * import { App} from './app.component';
 * import { getAppConfig } from './app.config';
 *
 * bootstrapApplication(App, getAppConfig());
 */
export const getAppConfig = (): ApplicationConfig => {
  const providers: (Provider | EnvironmentProviders)[] = [
    // Environment must be the first provider so EnvironmentService has the
    // correct apiUrl before any store or initializer that reads it.
    provideEnvironment(environment),

    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),

    provideAnalytics({
      disabled: isAnalyticsDisabledForEnv(environment.appEnv),
      posthogHost: environment.posthogHost,
      posthogKey: environment.posthogKey ?? '',
    }),

    // Indutrust brand: APP_BRAND_CONFIG, THEME_BRAND_PALETTES, PrimeNG Aura preset
    provideIndutrustBrandConfig({
      licenseKey: environment.primeNgLicenseKey,
    }),
    MessageService,

    // Apache ECharts (tree-shaken line/bar/pie core) for idt-chart-renderer
    provideCharts(),

    // Signal Forms (stable as of Angular 22) — opt-in error styling lives
    // on each shared form primitive so we leave the config empty here.
    provideSignalFormsConfig({}),

    // Auth
    { provide: AUTH_STRATEGY, useExisting: AuthApiClient },

    // Http — Fetch is the default backend in Angular 22.
    // Gap mocks use the HttpClient interceptor (not the browser SW): local
    // `apiUrl` is cross-origin staging, and the MSW worker skips those fetches.
    provideHttpClient(
      withInterceptors(
        useLegacyGapMocks
          ? [mockBackendInterceptor, authInterceptor]
          : [authInterceptor]
      ),
      withInterceptorsFromDi()
    ),

    // Routing
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      })
    ),

    // i18n
    translocoEnvironmentProvider(),
    I18nStore,

    // Signal Stores
    AppStore,
    AuthSessionStore,
    RouterStore,
    LayoutStore,
    ThemeStore,
    CompanyStore,
    CompanyFactoriesStore,
    CountryStore,
    IndustrialTypeStore,
    PlatformStore,
    TechnologyStore,
  ];

  // Local development against the real legacy API: fill the four surfaces
  // that have no uae-back equivalent (gallery images, machine datasheets,
  // VR capture requests). Demo builds register the full domain set via
  // `getDemoAppConfig()` + browser MSW instead.
  if (useLegacyGapMocks) {
    providers.unshift(
      provideMockBackendInterceptor(
        factoryLegacyGapHandlers(environment.apiUrl)
      )
    );
  }

  return {
    providers: providers,
  };
};
